import React from 'react';
import {useTranslation} from 'react-i18next';
import Dialog from 'components/Dialog';
import {DialogTypes} from 'common/names';
import {IAddablePgpKey} from 'types/AddablePgpKey';
import Panel from 'components/Panel';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Checkbox from '@mui/material/Checkbox';
import Button from 'components/Button';
import DialogActions from '@mui/material/DialogActions';
import FormikTextField from 'components/inputs/FormikTextField';
import FormikPassphrase from 'components/inputs/FormikPassphrase';
import {AlertDialog} from 'components/dialogs/AlertDialog';
import {useSelector} from 'react-redux';
import {layoutSelectors} from 'features/layout/redux';
import selectors, {LICENSE_UNLIMITED_NUM_KEYS} from 'redux/license/selectors';
import Typography from '@mui/material/Typography';
import FieldArray from 'components/hook-form/FieldArray';
import Formik from 'components/hook-form/Formik';

export type ImportOpenPGPKeysDialogResult = {keys: IAddablePgpKey[]};
export type ImportOpenPGPKeysDialogProps = {
   onClose: (result?: ImportOpenPGPKeysDialogResult) => void;
   data: {
      keys: IAddablePgpKey[];
      secret: boolean;
   };
};

const ImportOpenPGPKeysDialog = ({onClose, data: {keys, secret}}: ImportOpenPGPKeysDialogProps) => {
   const {t} = useTranslation();
   const license = useSelector(selectors.getLicense);
   const keysCount = useSelector(layoutSelectors.keysCountSelector);
   const [selected, setSelected] = React.useState<string[]>([]);
   const handleClick = (event: React.MouseEvent<unknown>, keyID: string) => {
      const selectedIndex = selected.indexOf(keyID);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
         newSelected = newSelected.concat(selected, keyID);
      } else if (selectedIndex === 0) {
         newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
         newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
         newSelected = newSelected.concat(
            selected.slice(0, selectedIndex),
            selected.slice(selectedIndex + 1)
         );
      }

      setSelected(newSelected);
   };

   const submit = React.useCallback(
      (values: {keys: IAddablePgpKey[]}) => {
         const selectedKeys = values.keys.filter((k) => selected.indexOf(k.keyID) >= 0);
         onClose({keys: selectedKeys});
      },
      [onClose, selected]
   );

   const isSelected = (keyID: string) => selected.indexOf(keyID) !== -1;

   if (keys?.length === 0) {
      return (
         <AlertDialog
            title={t('Import keys')}
            content={t('There are no keys to import.')}
            onClose={onClose}
         />
      );
   }
   const importKeysCount = keys?.length || 0;
   const allowedKeysCount = license.numKeys - keysCount.openpgp - selected.length;
   return (
      <Dialog maxWidth="lg" header={DialogTypes.ImportOpenPGPKeysDialog.dialogName} close={onClose}>
         {keys ? (
            <>
               {license.numKeys !== LICENSE_UNLIMITED_NUM_KEYS &&
               importKeysCount > allowedKeysCount ? (
                  <Typography>
                     {t(
                        'The keyring has {{importKeysCount}} keys. Your license only allows you to add {{allowedKeysCount}} at the current time',
                        {importKeysCount, allowedKeysCount}
                     )}
                  </Typography>
               ) : null}
               <Formik initialValues={{keys}} onSubmit={submit}>
                  <>
                     <Panel>
                        <TableContainer>
                           <Table size={'small'}>
                              <TableHead>
                                 <TableRow>
                                    <TableCell>{t('Import')}</TableCell>
                                    <TableCell>{t('Key ID')}</TableCell>
                                    <TableCell>{t('User ID')}</TableCell>
                                    <TableCell>{t('Diplomat Key Name')}</TableCell>
                                    {secret ? (
                                       <TableCell>{t('Imported Key Passphrase')}</TableCell>
                                    ) : null}
                                 </TableRow>
                              </TableHead>
                              <TableBody>
                                 <FieldArray
                                    name="keys"
                                    render={({fields}) =>
                                       fields.map((key, rowIndex) => {
                                          const keyID = ((key as unknown) as IAddablePgpKey).keyID;
                                          const userID = ((key as unknown) as IAddablePgpKey)
                                             .userID;
                                          const isItemSelected = isSelected(keyID);
                                          const labelId = `checkbox-${keyID}`;
                                          return (
                                             <TableRow tabIndex={-1} key={keyID}>
                                                <TableCell padding="checkbox">
                                                   <Checkbox
                                                      disabled={
                                                         !isItemSelected && allowedKeysCount === 0
                                                      }
                                                      checked={isItemSelected}
                                                      inputProps={{
                                                         'aria-labelledby': labelId,
                                                      }}
                                                      onClick={(event) => handleClick(event, keyID)}
                                                   />
                                                </TableCell>
                                                <TableCell>
                                                   {/* {Number(keyID)
                                                            .toString(16)
                                                            .toUpperCase()} */}
                                                   {keyID}
                                                </TableCell>
                                                <TableCell>{userID}</TableCell>
                                                <TableCell>
                                                   <FormikTextField
                                                      name={`keys.${rowIndex}.name`}
                                                      disabled={!isItemSelected}
                                                   />
                                                </TableCell>
                                                {secret ? (
                                                   <TableCell>
                                                      <FormikPassphrase
                                                         name={`keys.${rowIndex}.passphrase`}
                                                         disabled={!isItemSelected}
                                                      />
                                                   </TableCell>
                                                ) : null}
                                                {/* TODO check if existed */}
                                             </TableRow>
                                          );
                                       })
                                    }
                                 />
                              </TableBody>
                           </Table>
                        </TableContainer>
                     </Panel>
                     <DialogActions>
                        <Button type={'submit'} color="primary" disabled={selected.length === 0}>
                           {t('Ok')}
                        </Button>
                        <Button onClick={() => onClose()}>{t('Cancel')}</Button>
                     </DialogActions>
                  </>
               </Formik>
            </>
         ) : null}
      </Dialog>
   );
};

export default ImportOpenPGPKeysDialog;
